import { Reducer } from "redux";

import {
	Document,
	DocumentCount,
	DocumentSidePanelEnum,
	AutotaskProductValidationView
} from "@/Contracts/Documents";

import {
	DocumentsActionTypes,
	DocumentsActions,
	SetAiCoverImageUrlAction
} from "@/Actions/Documents";
import { RecentActivityItemView } from "@/Contracts/Others/RecentActivityFeed";
import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata
} from "@/Contracts/UI";
import { DocumentRevision } from "@/Contracts/Documents/Revision";

export interface DocumentsState {
	readonly isLoading: boolean;
	readonly documentList: Document[];
	readonly documentCount: DocumentCount;
	readonly currentActiveDocument: Document | null;
	readonly downloadDocumentModalOpen: boolean;
	readonly addDocumentDrawerOpen: boolean;
	readonly editOrCloneDocumentDrawerOpen: boolean;
	readonly quickbooksSyncDrawerOpen: boolean;
	readonly sendDocumentDrawerOpen: boolean;
	readonly extendDocumentExpiryDateModal: boolean;
	readonly activeDocumentPanel: DocumentSidePanelEnum | null;
	readonly emptyMergeTags: string[];
	readonly invalidAutotaskItems: AutotaskProductValidationView[];
	readonly isSignFlowConfirmationModalOpen: boolean;
	readonly isDocumentSidePanelOpen: boolean;
	readonly isPushToPSAModalOpen: boolean;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
	readonly isBulkActionsRecordsSelected: boolean;
	readonly previewPaymentSummary: boolean | null;
	readonly previewPaymentTerms: boolean | null;
	readonly isDocumentUnloading: boolean;
	readonly showPreviewDocumentModal: boolean;
	readonly showPreviewDocumentAsPdfModal: boolean;
	readonly aiCoverImageUrl: string | null;
	readonly recentActivityByDocumentId: Record<
		string,
		RecentActivityItemView | null
	>;
}

export const initialDocumentState: DocumentsState = {
	isLoading: false,
	documentList: new Array<Document>(),
	documentCount: new DocumentCount(),
	currentActiveDocument: null,
	downloadDocumentModalOpen: false,
	addDocumentDrawerOpen: false,
	editOrCloneDocumentDrawerOpen: false,
	quickbooksSyncDrawerOpen: false,
	sendDocumentDrawerOpen: false,
	activeDocumentPanel: null,
	extendDocumentExpiryDateModal: false,
	emptyMergeTags: new Array<string>(),
	invalidAutotaskItems: new Array<AutotaskProductValidationView>(),
	isSignFlowConfirmationModalOpen: false,
	isDocumentSidePanelOpen: true,
	isPushToPSAModalOpen: false,
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false,
	isBulkActionsRecordsSelected: false,
	previewPaymentSummary: null,
	previewPaymentTerms: null,
	isDocumentUnloading: false,
	showPreviewDocumentModal: false,
	aiCoverImageUrl: null,
	showPreviewDocumentAsPdfModal: false,
	recentActivityByDocumentId: {}
};

export const DocumentsReducer: Reducer<DocumentsState, DocumentsActions> = (
	state: DocumentsState = initialDocumentState,
	action: DocumentsActions
): DocumentsState => {
	switch (action.type) {
		case DocumentsActionTypes.SHOW_DOCUMENTS_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case DocumentsActionTypes.SHOW_DOCUMENTS_UNLOADER: {
			return {
				...state,
				isDocumentUnloading: true
			};
		}
		case DocumentsActionTypes.HIDE_DOCUMENTS_UNLOADER: {
			return {
				...state,
				isDocumentUnloading: false
			};
		}
		case DocumentsActionTypes.SHOW_PUSH_CHANGES_TO_PSA_MODAL: {
			return {
				...state,
				isPushToPSAModalOpen: true
			};
		}
		case DocumentsActionTypes.HIDE_PUSH_CHANGES_TO_PSA_MODAL: {
			return {
				...state,
				isPushToPSAModalOpen: false
			};
		}
		case DocumentsActionTypes.HIDE_DOCUMENTS_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case DocumentsActionTypes.DOCUMENTS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DOCUMENTS_SUCCESSFUL: {
			return {
				...state,
				documentList: action.documentList
			};
		}
		case DocumentsActionTypes.DOCUMENTS_FAILED: {
			return state;
		}
		case DocumentsActionTypes.DOCUMENTS_RESET: {
			return {
				...state,
				documentList: new Array<Document>(),
				recentActivityByDocumentId: {}
			};
		}
		case DocumentsActionTypes.DOCUMENT_DETAILS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DOCUMENT_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: action.currentActiveDocument
			};
		}
		case DocumentsActionTypes.DOCUMENT_DETAILS_FAILED: {
			return state;
		}
		case DocumentsActionTypes.DOCUMENT_DETAILS_UPDATED: {
			return {
				...state,
				currentActiveDocument: action.currentActiveDocument
			};
		}
		case DocumentsActionTypes.DOCUMENT_DETAILS_RESET: {
			return {
				...state,
				currentActiveDocument: null,
				activeDocumentPanel: null,
				downloadDocumentModalOpen: false,
				addDocumentDrawerOpen: false,
				editOrCloneDocumentDrawerOpen: false,
				isLoading: false
			};
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: null,
				documentList: state.documentList.filter(
					document => document.id !== action.deleteDocument.id
				)
			};
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.ADD_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.ADD_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				documentList: [action.addDocument].concat(state.documentList)
			};
		}
		case DocumentsActionTypes.ADD_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.EDIT_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.EDIT_DOCUMENT_SUCCESSFUL: {
			const documentList = state.documentList.filter(
				document => document.id !== action.editDocument.id
			);
			const updateDocumentIndex = state.documentList.findIndex(
				document => document.id === action.editDocument.id
			);
			if (updateDocumentIndex > -1) {
				documentList.splice(
					updateDocumentIndex,
					0,
					action.editDocument
				);
			}
			return {
				...state,
				documentList
			};
		}
		case DocumentsActionTypes.EDIT_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_CURRENT_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_CURRENT_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: action.updateCurrentDocument
			};
		}
		case DocumentsActionTypes.RENEW_CURRENT_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: action.updateCurrentDocument
			};
		}
		case DocumentsActionTypes.UPDATE_CURRENT_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.DOWNLOAD_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DOWNLOAD_DOCUMENT_SUCCESSFUL: {
			return state;
		}
		case DocumentsActionTypes.DOWNLOAD_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_COUNT: {
			return {
				...state,
				documentCount: action.documentCount
			};
		}
		case DocumentsActionTypes.RESET_DOCUMENT_COUNT: {
			return {
				...state,
				documentCount: new DocumentCount()
			};
		}
		case DocumentsActionTypes.MAIL_RECIPIENTS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DOWNLOAD_DOCUMENT_MODAL_OPENED: {
			return {
				...state,
				downloadDocumentModalOpen: true
			};
		}
		case DocumentsActionTypes.DOWNLOAD_DOCUMENT_MODAL_CLOSED: {
			return {
				...state,
				downloadDocumentModalOpen: false
			};
		}

		case DocumentsActionTypes.EXTEND_DOCUMENT_EXPIRY_DATE_MODAL_OPENED: {
			return {
				...state,
				extendDocumentExpiryDateModal: true
			};
		}
		case DocumentsActionTypes.EXTEND_DOCUMENT_EXPIRY_DATE_MODAL_CLOSED: {
			return {
				...state,
				extendDocumentExpiryDateModal: false
			};
		}

		case DocumentsActionTypes.ADD_DOCUMENT_DRAWER_OPENED: {
			return {
				...state,
				addDocumentDrawerOpen: true
			};
		}
		case DocumentsActionTypes.ADD_DOCUMENT_DRAWER_CLOSED: {
			return {
				...state,
				addDocumentDrawerOpen: false
			};
		}
		case DocumentsActionTypes.EDIT_OR_CLONE_DOCUMENT_DRAWER_OPENED: {
			return {
				...state,
				editOrCloneDocumentDrawerOpen: true
			};
		}
		case DocumentsActionTypes.EDIT_OR_CLONE_DOCUMENT_DRAWER_CLOSED: {
			return {
				...state,
				editOrCloneDocumentDrawerOpen: false
			};
		}
		case DocumentsActionTypes.SEND_DOCUMENT_DRAWER_OPENED: {
			return {
				...state,
				sendDocumentDrawerOpen: true
			};
		}
		case DocumentsActionTypes.SEND_DOCUMENT_DRAWER_CLOSED: {
			return {
				...state,
				sendDocumentDrawerOpen: false
			};
		}
		case DocumentsActionTypes.SET_DOCUMENT_SIDE_PANEL: {
			return {
				...state,
				activeDocumentPanel: action.documentSidePanel
			};
		}

		case DocumentsActionTypes.CANCEL_SIGNATURE_FROM_DOCUMENT_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.CANCEL_SIGNATURE_FROM_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: action.document
			};
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_DEFAULTS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_DEFAULTS_SUCCESSFUL: {
			const currentActiveDocument =
				state.currentActiveDocument as Document;
			const newRevisions = [...(currentActiveDocument.revisions || [])];
			newRevisions[0] = action.documentRevision;
			return {
				...state,
				currentActiveDocument: {
					...currentActiveDocument,
					revisions: newRevisions
				}
			};
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_DEFAULTS_FAILED: {
			return state;
		}
		case DocumentsActionTypes.CANCEL_SIGNATURE_FROM_DOCUMENT_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.UPDATE_MERGE_TAG_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_MERGE_TAG_FAILED: {
			return state;
		}
		case DocumentsActionTypes.CLONE_DOCUMENT_FAILED: {
			return state;
		}
		case DocumentsActionTypes.CLONE_DOCUMENT_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.CLONE_DOCUMENT_SUCCESSFUL: {
			return {
				...state,
				documentList: [action.cloneDocument, ...state.documentList]
			};
		}
		case DocumentsActionTypes.UPDATE_MERGE_TAG_SUCCESSFUL: {
			if (state.currentActiveDocument) {
				return {
					...state,
					currentActiveDocument: {
						...state.currentActiveDocument,
						merge_tag_custom_variables:
							action.customMergeTagVariables
					}
				};
			} else {
				return state;
			}
		}
		case DocumentsActionTypes.FETCH_EMPTY_MERGE_TAGS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_EMPTY_MERGE_TAGS_SUCCESSFUL: {
			return {
				...state,
				emptyMergeTags: action.emptyMergeTags.present_and_empty_tags
			};
		}
		case DocumentsActionTypes.FETCH_EMPTY_MERGE_TAGS_FAILED: {
			return state;
		}
		case DocumentsActionTypes.VALIDATE_AUTOTASK_ITEMS_SUCCESSFUL: {
			return {
				...state,
				invalidAutotaskItems: action.invalidProducts
			};
		}
		case DocumentsActionTypes.SHOW_SIGN_FLOW_CONFIRMATION_MODAL: {
			return {
				...state,
				isSignFlowConfirmationModalOpen: true
			};
		}
		case DocumentsActionTypes.HIDE_SIGN_FLOW_CONFIRMATION_MODAL: {
			return {
				...state,
				isSignFlowConfirmationModalOpen: false
			};
		}
		case DocumentsActionTypes.OPEN_DOCUMENT_SIDE_PANEL: {
			return {
				...state,
				isDocumentSidePanelOpen: true
			};
		}
		case DocumentsActionTypes.CLOSE_DOCUMENT_SIDE_PANEL: {
			return {
				...state,
				isDocumentSidePanelOpen: false
			};
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_VIEW_METADATA_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_VIEW_METADATA_SUCCESSFUL: {
			return {
				...state,
				viewMetadata: action.metadata
			};
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_VIEW_METADATA_FAILED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_ALL_DOCUMENT_VIEWS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_ALL_DOCUMENT_VIEWS_SUCCESSFUL: {
			return {
				...state,
				viewList: action.viewList
			};
		}
		case DocumentsActionTypes.FETCH_ALL_DOCUMENT_VIEWS_FAILED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_LIST_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_LIST_SUCCESSFUL: {
			return {
				...state,
				documentList: action.documentList.data,
				documentCount: action.documentList.filtered_count_map
			};
		}
		case DocumentsActionTypes.FETCH_DOCUMENT_LIST_FAILED: {
			return state;
		}
		case DocumentsActionTypes.CREATE_DOCUMENT_VIEW_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.CREATE_DOCUMENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case DocumentsActionTypes.CREATE_DOCUMENT_VIEW_FAILED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_VIEW_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_VIEW_FAILED: {
			return state;
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_VIEW_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case DocumentsActionTypes.DELETE_DOCUMENT_VIEW_FAILED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};
		}
		case DocumentsActionTypes.RESET_DOCUMENTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null,
				recentActivityByDocumentId: {}
			};
		}
		case DocumentsActionTypes.BULK_ACTION_RECORDS_SELECTED: {
			return {
				...state,
				isBulkActionsRecordsSelected: true
			};
		}
		case DocumentsActionTypes.BULK_ACTION_RECORDS_RESET: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case DocumentsActionTypes.BULK_DELETE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case DocumentsActionTypes.BULK_DELETE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.BULK_UPDATE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case DocumentsActionTypes.BULK_UPDATE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_REQUEST_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_REQUEST_SUCCESSFUL: {
			const updatedCurrentActiveDocument: Document | null =
				state.currentActiveDocument;

			if (updatedCurrentActiveDocument) {
				const updatedRevisionList: DocumentRevision[] = [
					...(updatedCurrentActiveDocument?.revisions ?? [])
				];
				updatedRevisionList[0] = {
					...updatedRevisionList[0],
					approval_details: { ...action.documentApproval }
				};

				updatedCurrentActiveDocument.revisions = [
					...updatedRevisionList
				];
			}

			return {
				...state,
				documentList: state.documentList.map(document => {
					if (document.id !== action.documentId) {
						return document;
					}

					const updatedDocument: Document = { ...document };
					const documentRevision: DocumentRevision[] = [
						...(updatedDocument.revisions ?? [])
					];
					documentRevision[0].approval_details = {
						...action.documentApproval
					};

					return updatedDocument;
				}),
				currentActiveDocument: state.currentActiveDocument
					? updatedCurrentActiveDocument
					: null
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_REQUEST_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_VERDICT_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_VERDICT_SUCCESSFUL: {
			const updatedCurrentActiveDocument: Document | null =
				state.currentActiveDocument;

			if (updatedCurrentActiveDocument) {
				const updatedRevisionList: DocumentRevision[] = [
					...(updatedCurrentActiveDocument?.revisions ?? [])
				];
				updatedRevisionList[0] = {
					...updatedRevisionList[0],
					approval_details: { ...action.documentApprovalVerdict }
				};

				updatedCurrentActiveDocument.revisions = [
					...updatedRevisionList
				];
			}

			return {
				...state,
				documentList: state.documentList.map(document => {
					if (document.id !== action.documentId) {
						return document;
					}

					const updatedDocument: Document = { ...document };
					const documentRevision: DocumentRevision[] = [
						...(updatedDocument.revisions ?? [])
					];
					documentRevision[0].approval_details = {
						...action.documentApprovalVerdict
					};

					return updatedDocument;
				}),
				currentActiveDocument: state.currentActiveDocument
					? updatedCurrentActiveDocument
					: null
			};
		}
		case DocumentsActionTypes.DOCUMENT_APPROVAL_VERDICT_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_FETCH_APPROVAL_STATUS_REQUESTED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.DOCUMENT_FETCH_APPROVAL_STATUS_SUCCESSFUL: {
			const updatedCurrentActiveDocument: Document | null =
				state.currentActiveDocument;

			if (updatedCurrentActiveDocument) {
				const updatedRevisionList: DocumentRevision[] = [
					...(updatedCurrentActiveDocument?.revisions ?? [])
				];
				updatedRevisionList[0] = {
					...updatedRevisionList[0],
					approval_details: {
						...action.documentApprovalApprovalDetails
							.approval_details
					}
				};

				updatedCurrentActiveDocument.revisions = [
					...updatedRevisionList
				];
				updatedCurrentActiveDocument.approval_required =
					action.documentApprovalApprovalDetails.approval_required;
				updatedCurrentActiveDocument.approval_required_rules = action
					.documentApprovalApprovalDetails.approval_required_rules
					? [
							...action.documentApprovalApprovalDetails
								.approval_required_rules
					  ]
					: null;
			}

			return {
				...state,
				currentActiveDocument: state.currentActiveDocument
					? updatedCurrentActiveDocument
					: null
			};
		}
		case DocumentsActionTypes.DOCUMENT_FETCH_APPROVAL_STATUS_FAILED: {
			return {
				...state
			};
		}
		case DocumentsActionTypes.MAKE_PAYMENT_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: { ...action.document }
			};
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_PO_DETAILS_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_PO_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: state.currentActiveDocument
					? {
							...state.currentActiveDocument,
							po_number: action.poNumber
					  }
					: null
			};
		}
		case DocumentsActionTypes.UPDATE_DOCUMENT_PO_DETAILS_FAILED: {
			return state;
		}

		case DocumentsActionTypes.BULK_ASSIGN_SIGNATURE_BLOCK_REQUESTED: {
			return state;
		}
		case DocumentsActionTypes.BULK_ASSIGN_SIGNATURE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				currentActiveDocument: action.document
			};
		}
		case DocumentsActionTypes.BULK_ASSIGN_SIGNATURE_BLOCK_FAILED: {
			return state;
		}
		case DocumentsActionTypes.UPDATE_PREVIEW_PAYMENT_SUMMARY: {
			return {
				...state,
				previewPaymentTerms: action.showPaymentTerms,
				previewPaymentSummary: action.showPaymentSummary
			};
		}

		case DocumentsActionTypes.QUICKBOOKS_SYNC_DRAWER_OPENED: {
			return {
				...state,
				quickbooksSyncDrawerOpen: true
			};
		}
		case DocumentsActionTypes.QUICKBOOKS_SYNC_DRAWER_CLOSED: {
			return {
				...state,
				quickbooksSyncDrawerOpen: false
			};
		}
		case DocumentsActionTypes.UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL: {
			const currentActiveDocument = state.currentActiveDocument
				? { ...state.currentActiveDocument }
				: null;
			if (!action.paymentSummary || !currentActiveDocument) {
				return {
					...state
				};
			}
			const { total, total_adjustment_fees, total_discount, total_tax } =
				action.paymentSummary;
			currentActiveDocument.total_discount = total_discount;
			currentActiveDocument.total_tax = total_tax;
			if (currentActiveDocument.revisions?.length) {
				currentActiveDocument.revisions[0].total = total ?? undefined;
			}
			if (currentActiveDocument.payment) {
				currentActiveDocument.payment.payment_options =
					total_adjustment_fees ?? undefined;
			}
			return {
				...state,
				currentActiveDocument: currentActiveDocument
			};
		}

		case DocumentsActionTypes.UPDATE_ORDER_FORM_PUBLISH_STATUS_REQUESTED: {
			return {
				...state,
				isLoading: true
			};
		}

		case DocumentsActionTypes.UPDATE_ORDER_FORM_PUBLISH_STATUS_SUCCESSFUL: {
			const currentActiveDocument = state.currentActiveDocument
				? { ...state.currentActiveDocument }
				: null;

			if (!currentActiveDocument) {
				return {
					...state,
					isLoading: false
				};
			}

			currentActiveDocument.status = action.documentStatus;

			return {
				...state,
				isLoading: false,
				currentActiveDocument
			};
		}

		case DocumentsActionTypes.UPDATE_ORDER_FORM_PUBLISH_STATUS_FAILED: {
			return {
				...state,
				isLoading: false
			};
		}

		case DocumentsActionTypes.SHOW_PREVIEW_DOCUMENT_MODAL: {
			return {
				...state,
				showPreviewDocumentModal: true
			};
		}
		case DocumentsActionTypes.HIDE_PREVIEW_DOCUMENT_MODAL: {
			return {
				...state,
				showPreviewDocumentModal: false
			};
		}
		case DocumentsActionTypes.SHOW_PREVIEW_DOCUMENT_AS_PDF_MODAL: {
			return {
				...state,
				showPreviewDocumentAsPdfModal: true
			};
		}
		case DocumentsActionTypes.HIDE_PREVIEW_DOCUMENT_AS_PDF_MODAL: {
			return {
				...state,
				showPreviewDocumentAsPdfModal: false
			};
		}

		case DocumentsActionTypes.SET_AI_COVER_IMAGE_URL: {
			return {
				...state,
				aiCoverImageUrl: (action as SetAiCoverImageUrlAction).payload
			};
		}

		case DocumentsActionTypes.MERGE_RECENT_ACTIVITY_FOR_DOCUMENTS: {
			return {
				...state,
				recentActivityByDocumentId: {
					...state.recentActivityByDocumentId,
					...action.map
				}
			};
		}

		default: {
			return state;
		}
	}
};

export default DocumentsReducer;
